<template>
	<modal
		v-if="modalProps"
		class="checkout-success-modal"
		v-bind="modalProps"
		@close="closeModal"
	>
		<div>
			<template v-if="isPosStaffMessage">
				<div class="staff-close-message">
					<span class="staff-close-label">Your tab at</span>
					<span class="staff-close-highlight">{{ venueName }}</span>
					<span class="staff-close-label">was closed by</span>
					<span class="staff-close-highlight">{{ normalizedStaffName }}</span>
				</div>
			</template>
			<template v-if="isSuccessMessage">
				<img :src="icon" alt="" class="check-image" />
				<span class="success-text">Success</span>
				<p>Your order has been successfully placed!</p>
			</template>
			<div v-if="isSendReceipt" :class="{ disabled: isLoading }">
				<Input
					:value="currentEmail"
					:placeholder="'Enter your email'"
					:auto-select-text="true"
					:show-counter="false"
					@input="setEmail"
					@enter="sendReceipt"
				/>
			</div>
		</div>
	</modal>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import Input from '../forms/Input'
import Modal from './Modal.vue'

import { emailValidation } from '@/utils/helpers'
import storage from '@/utils/storage'

import SuccessIcon from '../../assets/images/ic_check.svg'

export default {
	name: 'SuccessCheckoutModal',
	components: {
		Input,
		Modal
	},
	props: {
		cartId: {
			type: String,
			required: true
		},
		isShowSendReceipt: {
			type: Boolean,
			default: false
		},
		mode: {
			type: String,
			default: 'success'
		},
		venueName: {
			type: String,
			default: ''
		},
		staffName: {
			type: String,
			default: 'Server'
		},
		darkTheme: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			currentEmail: '',
			isSuccessMessage: true,
			isSendReceipt: false,
			isLoading: false
		}
	},

	computed: {
		...mapState('cart', ['email', 'hadSentAutoEmail']),

		isPosStaffMessage() {
			return this.mode === 'posStaff'
		},

		normalizedStaffName() {
			if (!this.staffName) return 'Server'

			return this.staffName
		},

		icon() {
			return SuccessIcon
		},

		modalProps() {
			// Not for production, only this logic only for Expo 2026.
			if (this.isPosStaffMessage) {
				return {
					title: '',
					firstButtonText: 'OK',
					firstButtonAction: this.confirmStaffClosedTab,
					isLoading: this.isLoading,
					darkTheme: this.darkTheme
				}
			}

			if (this.isSuccessMessage) {
				return {
					title: '',
					firstButtonText: 'DONE',
					secondButtonText: this.hadSentAutoEmail
						? 'RESEND RECEIPT'
						: 'SEND RECEIPT',
					firstButtonAction: this.closeModal,
					secondButtonAction: this.showSendReceipt,
					isLoading: this.isLoading,
					darkTheme: this.darkTheme
				}
			}

			if (this.isSendReceipt) {
				return {
					title: this.hadSentAutoEmail ? 'Resend Receipt' : 'Send Receipt',
					firstButtonText: 'CANCEL',
					secondButtonText: 'SEND',
					firstButtonAction: this.closeModal,
					secondButtonAction: this.sendReceipt,
					isLoading: this.isLoading,
					darkTheme: this.darkTheme
				}
			}

			return null
		}
	},

	mounted() {
		this.currentEmail = this.email || storage.getStorage('email')

		if (this.isPosStaffMessage) {
			this.isSuccessMessage = false
			this.isSendReceipt = false
		} else if (this.isShowSendReceipt) {
			this.isSuccessMessage = false
			this.isSendReceipt = true
		}
	},

	methods: {
		...mapActions('cart', ['sendPaymentReceipt', 'setReceiptShown']),
		...mapActions('ui', ['addMainError']),

		submitForm() {
			this.$refs.form.sumbit(e => e.prevent.default)
		},

		setEmail(val) {
			this.currentEmail = val
		},

		showSendReceipt() {
			this.isSuccessMessage = false
			this.isSendReceipt = true
		},

		confirmStaffClosedTab() {
			this.$emit('staff-close-confirm')
		},

		closeModal() {
			this.setReceiptShown(this.cartId)
			this.$emit('close')
		},

		validateEmail(email) {
			return emailValidation(email)
		},

		async sendReceipt() {
			this.setReceiptShown(this.cartId)
			if (!this.currentEmail) {
				this.addMainError('Email required')
				return
			}
			if (!this.validateEmail(this.currentEmail)) {
				this.addMainError('Email invalid')
				return
			}

			try {
				this.isLoading = true
				await this.sendPaymentReceipt({
					cartId: this.cartId,
					email: this.currentEmail
				})
				this.isLoading = false
				this.closeModal()
			} catch (e) {
				this.isLoading = false
			}
		}
	}
}
</script>

<style scoped lang="scss">
.checkout-success-modal.modal {
	.disabled {
		pointer-events: none;
	}

	.check-image {
		margin: 0 0 14px;
	}

	.input-field {
		width: auto;
		border-bottom: 1px solid #554781;

		/deep/ input {
			color: #1d1b25 !important;
		}
	}

	.success-text {
		display: block;
		font-size: 16px;
		color: #4ac692;
		padding: 0 0 20px;
	}

	.staff-close-message {
		display: flex;
		flex-direction: column;
		align-items: center;
		margin: 0 0 20px;
		text-align: center;
	}

	.staff-close-label {
		display: block;
		margin-bottom: 10px;
		font-size: 16px;
		line-height: 1.35;
		opacity: 0.8;
	}

	.staff-close-highlight {
		display: block;
		margin-bottom: 7px;
		font-size: 18px;
		line-height: 1.35;
		font-weight: 700;
	}

	p {
		margin: 0 0 20px;
	}
}
</style>
